import React, {useEffect, useMemo, useState} from 'react';

import {useCurrentUserState} from 'app/current-user/api';
import {gt} from 'app/utils/i18n';

import {AssignableLists} from './assignables-lists/AssignableLists';
import {AssignToStepsNewFeatureAlert} from './AssignToStepsNewFeatureAlert';
import {GlobalAssignUsersField} from './GlobalAssignUsersField';
import {useAssignments, useEffectAfterMounted} from './use-assignees';
import {useDocumentStepsAndLanes} from './use-document-steps-and-lanes';
import {UseLanesHintMessage} from './UseLanesHintMessage';
import {hasAnyAssignee} from './utils';

import {CheckboxWithSubFields} from '../DueDateRecurringForm/RRuleForm/CheckboxWithSubFields';

import type {AssignedDocType} from '../PickDocumentForm';
import type {AllAssignmentsType} from './types';
import type {WizardFormPaneProps} from 'app/general/widgets/WizardAccordion';

// this is what this form sends back to the BaseAssignTaskModal

interface Props extends WizardFormPaneProps<AllAssignmentsType> {
  document: AssignedDocType;
  canMultiAssign: boolean;
  initial: AllAssignmentsType;
  isMultiAssign: boolean;
  setMultiAssign: (isMulti: boolean) => void;
  permission: 'view' | 'retrieve';
  setPermission: (permission: 'view' | 'retrieve') => void;
  canGoToPrevious: boolean;
}

export const AssignForm = function AssignForm({
  document,
  canMultiAssign,
  FooterComponent,
  initial,
  isMultiAssign,
  setMultiAssign,
  permission,
  setPermission,
  canGoToPrevious,
}: Props) {
  const [showGranularAssign, setShowGranularAssign] = useState(
    (initial.granular.length ?? 0) > 0
  );
  const {assignments, initialGranular, setAssignments} =
    useAssignments(initial);
  const {steps, lanes} = useDocumentStepsAndLanes(document);

  // toggle show-granular checkbox after loading task instance data
  useEffectAfterMounted(() => {
    if (initialGranular.length > 0 && !showGranularAssign) {
      setShowGranularAssign(true);
    } else if (initialGranular.length === 0 && showGranularAssign) {
      setShowGranularAssign(false);
    }
  }, [initialGranular.length]);

  useEffectAfterMounted(() => {
    // reset assigned state when doc is changed
    setAssignments(null);
  }, [steps, lanes]);

  useSelfAssign(document.can_assign, setAssignments);

  useEffect(() => {
    if (isMultiAssign && showGranularAssign) {
      // disable granular-assign when multi-assigning
      setShowGranularAssign(false);
    }
  }, [isMultiAssign]);

  const hasAssignees = useMemo(
    () => hasAnyAssignee(assignments.global),
    [assignments.global]
  );

  const hasDirectAssignees = useMemo(
    () => assignments.global.users.length > 0,
    [assignments.global.users]
  );

  return (
    <form>
      {/*<pre>{JSON.stringify(initial, null, 2)}</pre>*/}
      <>
        {document.can_assign && canMultiAssign ? (
          <>
            <div className="form-group mb-4">
              <label htmlFor="multi-assign-field" className="d-sm-none">
                {gt('Assign Mode')}
              </label>
              <div className="input-group mb-3">
                <div className="input-group-prepend d-none d-sm-block">
                  <label
                    className="input-group-text"
                    htmlFor="multi-assign-field"
                  >
                    {gt('Assign')}
                  </label>
                </div>
                <select
                  value={isMultiAssign ? 1 : 0}
                  onChange={() => setMultiAssign(!isMultiAssign)}
                  className="custom-select"
                  id="multi-assign-field"
                >
                  <option value={0}>{gt('Teammates assigned together')}</option>
                  <option value={1}>
                    {gt('Teammates assigned their own task')}
                  </option>
                </select>
              </div>
              {isMultiAssign ? (
                <div className="alert alert-info small mt-3" role="alert">
                  {gt(
                    'A separate task will be assigned to each teammate, team member and role member.'
                  )}
                </div>
              ) : null}
            </div>
          </>
        ) : !document.can_assign ? (
          <div className="alert alert-warning my-4 small" role="alert">
            {gt('The selected document can only be assigned to yourself')}
          </div>
        ) : null}
        {document.can_assign && !isMultiAssign ? (
          <div className="mt-3">
            <hr />
            <AssignToStepsNewFeatureAlert />
            <CheckboxWithSubFields
              fieldId="granular-assign"
              label={gt('Assign to steps and lanes.')}
              checked={showGranularAssign}
              onChange={(granular) => {
                setAssignments({
                  ...assignments,
                  // when granular assign is disabled all granular assignments are removed as well
                  // when granular assign is enabled, the initial granular assignment is recovered
                  // that's useful when reassigning tasks with granular assignments
                  granular: granular ? initialGranular : [],
                });
                setShowGranularAssign(granular);
              }}
              opened={showGranularAssign}
            >
              <AssignableLists
                steps={steps}
                lanes={lanes}
                assignments={assignments.granular}
                setAssignments={setAssignments}
              />
            </CheckboxWithSubFields>
          </div>
        ) : null}
        <hr className="mt-0" />
        <GlobalAssignUsersField
          locked={!document.can_assign}
          assignments={assignments}
          onChange={setAssignments}
          field={['user_ids', 'team_ids', 'role_ids']}
          noMargin
        />
        {hasDirectAssignees ? (
          <div className="form-row">
            <div className="form-group col-12">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={'readOnlyTaskId'}
                  onChange={(e) =>
                    setPermission(e.target.checked ? 'retrieve' : 'view')
                  }
                  checked={permission === 'retrieve'}
                />
                <label className="form-check-label" htmlFor={'readOnlyTaskId'}>
                  {gt('Assign with read-only permission')}
                </label>
              </div>
            </div>
          </div>
        ) : null}
      </>
      <UseLanesHintMessage lanes={lanes} assignments={assignments.granular} />
      <FooterComponent
        isCompleted={hasAssignees}
        result={assignments}
        showSeparator={true}
        canGoToPrevious={canGoToPrevious}
      />
    </form>
  );
};

/**
 * Hook to provide all data required to perform the self-assignment
 * It will:
 * 1. get current user data
 * 2. set current user as document assignee
 */
function useSelfAssign(
  can_assign: boolean,
  setAssignments: (
    fn: (assignmments: AllAssignmentsType) => AllAssignmentsType
  ) => unknown
) {
  const currentUser = useCurrentUserState().data!;

  useEffect(() => {
    if (!can_assign) {
      setAssignments((assignments) => ({
        ...assignments,
        global: {
          ...assignments.global,
          users: [currentUser],
        },
      }));
    }
  }, [currentUser?.id, can_assign]);
}
